import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CheckBoxOutlinedIcon from "@material-ui/icons/CheckBoxOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CompareArrowsIcon from "@material-ui/icons/CompareArrows";
import FlightTakeoffIcon from "@material-ui/icons/FlightTakeoff";
import ListAltIcon from "@material-ui/icons/ListAlt";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import TouchAppIcon from "@material-ui/icons/TouchApp";
import WarningIcon from "@material-ui/icons/Warning";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(4),
        maxWidth: 900,
        margin: '0 auto',
    },
    titleUnderline: {
        width: 48,
        height: 4,
        backgroundColor: theme.palette.primary.main,
        borderRadius: 2,
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(0.5),
    },
    card: {
        display: 'flex',
        alignItems: 'flex-start',
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: 8,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(1.5),
        gap: theme.spacing(2.5),
    },
    iconWrapper: {
        flexShrink: 0,
        width: 52,
        height: 52,
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
    },
    cardTitle: {
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
    },
    cardDescription: {
        color: theme.palette.text.secondary,
        lineHeight: 1.6,
    },
    importantCard: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(81,164,45,0.08)',
        border: `1px solid ${theme.palette.primary.main}55`,
        borderRadius: 8,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(3),
        gap: theme.spacing(3),
    },
    importantLeft: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        gap: theme.spacing(0.5),
    },
    importantTitle: {
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: '1rem',
    },
    importantItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.75),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    checkIcon: {
        color: theme.palette.primary.main,
        fontSize: 20,
        flexShrink: 0,
    },
    warningIcon: {
        color: theme.palette.primary.main,
        fontSize: 40,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
        gap: theme.spacing(2),
    },
    footerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    footerIcon: {
        color: theme.palette.primary.main,
        fontSize: 52,
    },
    startButton: {
        fontWeight: 700,
        letterSpacing: 1,
        padding: theme.spacing(1.5, 4),
        whiteSpace: 'nowrap',
    },
}));

const INSTRUCTION_CARDS = [
    {
        icon: TouchAppIcon,
        title: 'Como responder',
        description: 'Selecione uma alternativa para cada questão. Você poderá alterar sua resposta a qualquer momento antes da finalização do simulado.',
    },
    {
        icon: CompareArrowsIcon,
        title: 'Navegação',
        description: 'Utilize os botões "Anterior" e "Próxima" ou clique diretamente no número da questão na barra superior para navegar entre as perguntas.',
    },
    {
        icon: ListAltIcon,
        title: 'Acompanhamento',
        description: 'A barra superior permite visualizar todas as questões e identificar rapidamente qual está sendo respondida, qual já foi respondida e as que ainda não foram respondidas.',
    },
    {
        icon: AccessTimeIcon,
        title: 'Tempo de realização',
        description: 'Caso o simulado possua limite de tempo, o cronômetro será exibido no canto superior direito da tela. Certifique-se de concluir suas respostas dentro do prazo estabelecido.',
    },
    {
        icon: CheckBoxOutlinedIcon,
        title: 'Finalização',
        description: <>O botão <strong>"Finalizar"</strong> será habilitado somente após o preenchimento de todas as questões. Após a finalização, as respostas serão enviadas para correção e não poderão ser alteradas.</>,
    },
];

const IMPORTANT_ITEMS = [
    'Verifique suas respostas antes de finalizar o simulado.',
    'Mantenha uma conexão estável com a internet durante a realização da atividade.',
    'Evite fechar ou atualizar a página durante o simulado.',
];

export function TestInstructionsComponent({ handleStartTest, loading }) {
    const classes = useStyles();

    return (
        <Box className={classes.root}>
            <Typography variant="h4" style={{ fontWeight: 700, letterSpacing: 2 }}>
                INSTRUÇÕES
            </Typography>
            <div className={classes.titleUnderline} />
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
                Leia atentamente as orientações antes de iniciar o simulado.
            </Typography>

            {INSTRUCTION_CARDS.map(({ icon: Icon, title, description }) => (
                <Box key={title} className={classes.card}>
                    <Box className={classes.iconWrapper}>
                        <Icon fontSize="default" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" className={classes.cardTitle}>
                            {title}
                        </Typography>
                        <Typography variant="body2" className={classes.cardDescription}>
                            {description}
                        </Typography>
                    </Box>
                </Box>
            ))}

            <Box className={classes.importantCard}>
                <Box className={classes.importantLeft}>
                    <WarningIcon className={classes.warningIcon} />
                    <Typography className={classes.importantTitle}>Importante</Typography>
                </Box>
                <Box>
                    {IMPORTANT_ITEMS.map((item) => (
                        <Box key={item} className={classes.importantItem}>
                            <CheckCircleOutlineIcon className={classes.checkIcon} />
                            <Typography variant="body2">{item}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box className={classes.footer}>
                <Box className={classes.footerLeft}>
                    <FlightTakeoffIcon className={classes.footerIcon} />
                    <Typography variant="body2" color="textSecondary">
                        Quando estiver pronto(a), clique em{' '}
                        <strong>Iniciar Avaliação</strong> para começar.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    className={classes.startButton}
                    onClick={handleStartTest}
                    disabled={loading}
                    startIcon={loading ? null : <PlayArrowIcon />}
                >
                    {loading
                        ? <><CircularProgress size={18} />&nbsp;Gerando avaliação</>
                        : 'INICIAR AVALIAÇÃO'
                    }
                </Button>
            </Box>
        </Box>
    );
}
